// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Box } from '@mui/material';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { useEffect, useRef } from 'react';
import { useCopilotChat } from '../hooks/use-copilot-chat';
import { CopilotChatInput } from './copilot-chat-input';
import { CopilotChatMessage } from './copilot-chat-message';
import { ResizableChatWindow } from './resizable-chat-window';

export const CopilotChat = () => {
  const {
    isChatOpen,
    messages,
    toggleChat,
    handleSendMessage,
    loading: isLoadingMessage,
    notification,
  } = useCopilotChat();

  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current !== null) {
      chatContainerRef.current.scrollTo({
        behavior: 'smooth',
        top: chatContainerRef.current.scrollHeight,
      });
    }
  }, [messages, isChatOpen]);

  return (
    <Box>
      <div className="fixed bottom-7 right-6 z-50">
        <IconButton
          variant="icon"
          icon="CopilotChatButton"
          className=" !text-copilot-icon hover:!text-secondary-dark-mui hover:!text-opacity-100 hover:!opacity-100"
          size={48}
          onClick={toggleChat}
        />
        <span
          className="absolute -right-1 -top-1 flex h-6 w-6 animate-fade shadow-lg aria-hidden:hidden"
          aria-hidden={!notification}
          data-testid="copilot-notification"
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-copilot-notification opacity-75"></span>
          <span className="relative inline-flex h-6 w-6 rounded-full bg-copilot-notification"></span>
        </span>
      </div>

      {isChatOpen && (
        <ResizableChatWindow>
          <div className="m-2 flex flex-1 flex-col gap-y-2 overflow-y-auto rounded-lg bg-white pb-2.5">
            <div
              className="flex grow flex-col overflow-y-scroll"
              data-testid="copilot-chat-test-id"
              data-scrollable={false}
              ref={chatContainerRef}
            >
              {messages.map((msg, index) => (
                <CopilotChatMessage
                  key={index}
                  message={msg.message}
                  sender={msg.sender}
                  allowCopy={index !== 0}
                />
              ))}
              {isLoadingMessage && (
                <CopilotChatMessage message="" sender="CITEC" loading />
              )}
            </div>
            <div className="flex items-center gap-x-2.5 pl-2 pr-6">
              <CopilotChatInput handleSendMessage={handleSendMessage} />
              <IconButton
                icon="CopilotChatBackButton"
                className="!rounded-full"
                onClick={toggleChat}
              />
            </div>
          </div>
        </ResizableChatWindow>
      )}
    </Box>
  );
};
